<script setup>
import { useStore } from 'vuex';
import { ref, defineEmits, defineExpose, onMounted, computed } from 'vue';
import { AppButton, DropdownSelectable, Input } from '@n11/so-ui-components';

const store = useStore();

const emit = defineEmits(['filterEmitter']);

const filterProductName = ref('');
const filterProductCode = ref('');
const filterStockCode = ref('');
const filterGtin = ref('');
const filterBrandName = ref('');
const filterStatus = ref('');
const dropdown = ref(null);
const filterCategories = ref([]);
const selectedCategoryIds = ref([]);
const dropdownSelectableCategoriesRef = ref(null);

const isFilterEmpty = computed(() => {
  return (
    !filterProductName.value.trim() &&
    !filterProductCode.value &&
    !filterStockCode.value.trim() &&
    !filterGtin.value &&
    !filterBrandName.value.trim() &&
    !filterStatus.value &&
    selectedCategoryIds.value.length === 0
  );
});

const filterEmitter = () => {
  emit('filterEmitter', {
    productName: filterProductName.value,
    brandName: filterBrandName.value,
    searchCategoryIds: selectedCategoryIds.value,
    productId: filterProductCode.value,
    sellerStockCode: filterStockCode.value,
    gtin: filterGtin.value,
    status: filterStatus.value,
  });
};

const selectCategory = (items) => {
  if (Array.isArray(items)) {
    selectedCategoryIds.value = items.map((item) => item.id);
  } else {
    selectedCategoryIds.value = [];
  }
};

const selectStatus = (e) => {
  filterStatus.value = e?.id ?? [];
};

const clearFiltersAndTable = () => {
  clearFilters();
  filterEmitter();
};

const clearFilters = () => {
  filterProductName.value = '';
  filterProductCode.value = '';
  filterStockCode.value = '';
  filterGtin.value = '';
  filterBrandName.value = '';
  selectedCategoryIds.value = [];
  if (dropdownSelectableCategoriesRef.value) {
    dropdownSelectableCategoriesRef.value.clearSelection();
    dropdownSelectableCategoriesRef.value.handleDeleteSearchValue();
  }

  resetStatusDropdown();
};

const resetStatusDropdown = () => {
  filterStatus.value = '';
  dropdown.value?.clearSelection();
};

const getCategories = async () => {
  try {
    const response = await store.dispatch('categories/getCategories');
    filterCategories.value = response?.categories?.map((category) => ({
      id: category.id,
      name: category.name,
    }));
  } catch (error) {
    console.error('Kategoriler yüklenemedi:', error);
    return [];
  }
};

onMounted(async () => {
  await getCategories();
});

defineExpose({ clearFilters });
</script>

<template>
  <div>
    <p class="title">
      Ürünleri Filtrele
    </p>
    <div class="filters-box">
      <div class="filter-inputs">
        <Input
          id="product_name"
          v-model:value="filterProductName"
          label="Ürün Adı"
          type="text"
        />
        <Input
          id="product_code"
          v-model:value="filterProductCode"
          label="Ürün Kodu"
          type="text"
          :min-length="7"
          :is-only-number="true"
        />
        <Input
          id="stock_code"
          v-model:value="filterStockCode"
          label="Stok Kodu"
          type="text"
        />
        <Input
          id="gtin_barcod"
          v-model:value="filterGtin"
          label="GTIN (Global Barkod)"
          type="text"
          :max-length="14"
          :is-only-number="true"
        />
      </div>

      <div class="dropdown">
        <Input
          id="brand_name"
          v-model:value="filterBrandName"
          label="Marka"
          type="text"
        />
        <DropdownSelectable
          ref="dropdownSelectableCategoriesRef"
          :drop-items="filterCategories"
          label="Kategori"
          not-found-text="Kategori bulunamadı."
          custom-input-label="Yeni Değer Ekle"
          size="md"
          :single-item-text-length="80"
          :should-show-empty-icon="true"
          custom-value-placeholder="Yeni Değer ekle"
          @selected-items="selectCategory"
        />
        <DropdownSelectable
          ref="dropdown"
          :drop-items="[
            { id: '', name: 'Tüm İşlemler' },
            { id: 'ACCEPTED', name: 'Onayladığım Teklifler' },
            { id: 'REFUSED', name: 'Vazgeçtiğim Teklifler' },
            { id: 'WAITING_FOR_ACCEPTANCE', name: 'Onay Bekleyen Teklifler' },
          ]"
          :is-show-search="false"
          :is-radio-group="true"
          label="Tüm İşlemler"
          @selected-radio-item="selectStatus"
        />
        <div class="checkbox-buttons">
          <AppButton
            secondary
            sm
            title="Temizle"
            @click="clearFiltersAndTable"
          />
          <AppButton
            primary
            sm
            title="Filtrele"
            :disabled="isFilterEmpty"
            @click="filterEmitter"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.title {
  margin-bottom: 24px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.05px;
  line-height: 20px;
}

.filters-box {
  width: 100%;

  .filter-inputs {
    display: flex;
    height: 40px;
    margin-bottom: 16px;
    gap: 16px;

    input {
      display: flex;
      width: 20%;
      padding: 12px;
      border: 0.5px solid $colorD30;
      border-radius: 6px;
      background: $colorN00;

      &::placeholder {
        color: $colorD40;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
      }
    }
  }

  > .dropdown {
    display: flex;
    justify-content: space-between;
    gap: 16px;

    /* stylelint-disable-next-line selector-class-pattern -- temporary ignored, side-effect checks needed */
    .dropdownSelectable {
      width: 500px;
    }

    .checkbox-buttons {
      display: flex;
      align-items: end;
      justify-content: end;
      gap: 16px;

      button {
        height: 42px;
      }
    }

    > * {
      flex: 1;
    }
  }
}

.btn.primary {
  display: flex;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 7px 32px;
  border-radius: 4px;
  background: $colorN80;
  color: #fff;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  gap: 2px;
  letter-spacing: -0.05px;
  line-height: 18px;

  &:disabled {
    background: $colorN40;
  }
}

.secondary {
  display: flex;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 7px 32px;
  border-radius: 4px;
  background: $colorN40;
  color: #fff;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  gap: 2px;
  letter-spacing: -0.05px;
  line-height: 18px;
}
</style>
